import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, forwardRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ClientOption, ClientOptionsApi } from './client-options-api';

@Component({
  selector: 'app-client-select',
  imports: [CommonModule, FormsModule],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => ClientSelect), multi: true }],
  styles: [':host { display: block; } select { width: 100%; }'],
  template: `
    <select aria-label="Cliente" [ngModel]="value" (ngModelChange)="select($event)"
      [ngModelOptions]="{standalone: true}" [disabled]="disabled || loading || !!error" (blur)="onTouched()">
      <option [ngValue]="null">{{ loading ? 'Cargando clientes...' : 'Seleccionar cliente' }}</option>
      <option *ngIf="value !== null && !loading && !hasSelectedClient" [ngValue]="value" disabled>
        Cliente {{ value }} (no disponible)
      </option>
      <option *ngFor="let client of clients" [ngValue]="client.id">{{ client.razon_social || 'Cliente ' + client.id }}</option>
    </select>
    <small *ngIf="!loading && !error && clients.length === 0">No hay clientes habilitados.</small>
    <span *ngIf="error" role="alert">{{ error }} <button type="button" (click)="load()">Reintentar</button></span>
  `
})
export class ClientSelect implements ControlValueAccessor, OnInit {
  private readonly api = inject(ClientOptionsApi);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  clients: ClientOption[] = [];
  value: number | null = null;
  disabled = false;
  loading = true;
  error = '';
  onChange: (value: number | null) => void = () => {};
  onTouched: () => void = () => {};

  get hasSelectedClient(): boolean {
    return this.clients.some(client => client.id === this.value);
  }

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading = true;
    this.error = '';
    this.api.clients$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: clients => {
        this.clients = clients;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.error = 'No se pudieron cargar los clientes.';
        this.cdr.markForCheck();
      }
    });
  }

  writeValue(value: number | null): void { this.value = value ?? null; this.cdr.markForCheck(); }
  registerOnChange(fn: (value: number | null) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(disabled: boolean): void { this.disabled = disabled; this.cdr.markForCheck(); }
  select(value: number | null): void {
    this.value = value;
    this.onChange(value);
    this.onTouched();
  }
}
